import ArticleLayout from './ArticleLayout';

export default function ShippingGuide() {
  return (
    <ArticleLayout
      title="Sugargoo Shipping Guide: Lines, Costs, and Delivery Times Explained"
      subtitle="Navigate Sugargoo shipping options with confidence. Our detailed guide compares all major shipping lines, explains costs and delivery times by region, and shares tips to save money on international shipping for your spreadsheet finds."
      category="Shipping Guide"
      breadcrumbs={[
        { name: 'Home', path: '/' },
        { name: 'Shipping Guide', path: '/sugargoo-shipping-guide' },
      ]}
      schemaJson={{
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'Sugargoo Shipping Guide: Lines, Costs, and Delivery Times Explained',
        description: 'Navigate Sugargoo shipping options with our detailed guide comparing all major shipping lines, costs, and delivery times by region.',
        author: { '@type': 'Organization', name: 'Sugargoo Spreadsheet Watch' },
        datePublished: '2026-06-12',
        dateModified: '2026-06-18',
        publisher: { '@type': 'Organization', name: 'Sugargoo Spreadsheet Watch' },
      }}
    >
      <section className="mb-12">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">Understanding Sugargoo Shipping Options</h2>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
          Once your items have passed QC inspection and you are ready to ship, choosing the right shipping line becomes the next critical decision. Sugargoo offers a variety of international shipping options, each with different price points, delivery speeds, and reliability levels. Understanding these differences will help you balance cost and speed based on your specific needs.
        </p>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
          Shipping costs are calculated based on the actual weight or volumetric weight of your package (whichever is greater), the destination country, and the shipping line selected. Some lines also have restrictions on certain types of products, so always check compatibility before finalizing your shipment.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">Shipping Line Comparison</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-200">
                <th className="text-left py-3 px-4 font-semibold text-foreground-900">Shipping Line</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground-900">Speed</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground-900">Cost Level</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground-900">Best For</th>
              </tr>
            </thead>
            <tbody>
              {[
                { line: 'EMS', speed: '7-15 days', cost: 'Medium', best: 'Balanced option, most countries' },
                { line: 'EUB', speed: '10-20 days', cost: 'Low', best: 'Small packages under 2kg' },
                { line: 'DHL/FedEx', speed: '3-7 days', cost: 'High', best: 'Fast delivery, premium service' },
                { line: 'SAL', speed: '15-30 days', cost: 'Very Low', best: 'Budget shipping, patient buyers' },
                { line: 'Sea Freight', speed: '30-60 days', cost: 'Lowest', best: 'Large hauls, no rush' },
              ].map((row) => (
                <tr key={row.line} className="border-b border-background-100 hover:bg-background-100/50 transition-colors">
                  <td className="py-3 px-4 font-medium text-foreground-900">{row.line}</td>
                  <td className="py-3 px-4 text-foreground-600">{row.speed}</td>
                  <td className="py-3 px-4 text-foreground-600">{row.cost}</td>
                  <td className="py-3 px-4 text-foreground-600">{row.best}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">Money-Saving Shipping Tips</h2>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
          To minimize shipping costs, consider consolidating multiple items into a single package whenever possible. Sugargoo offers free warehouse storage for a limited time, allowing you to accumulate several spreadsheet finds before shipping them together. This approach almost always results in lower per-item shipping costs compared to sending items individually.
        </p>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
          Additionally, remove unnecessary packaging (shoe boxes, brand bags) through Sugargoo&rsquo;s package removal service to reduce volumetric weight. For many items, especially clothing and soft goods, removing excess packaging can cut shipping costs by 20-30% without affecting product quality during transit.
        </p>
      </section>
    </ArticleLayout>
  );
}